import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Link, useParams } from 'react-router-dom';
import { useHistory } from 'react-router';
import {
  DeprecatedButton as Button,
  ActionItem,
  HeadingText50,
  DeprecatedInlineMessage as InlineMessage,
} from '@modernhealth/vital-components';
import { layout } from '@modernhealth/vital-foundation';
import { captureException as sentryCaptureException } from '@sentry/react';
import { useAnalytics } from '@modernhealth/web-analytics';

import { captureException } from 'src/shared/utils/captureException';

import { ReactComponent as Image } from '../assets/images/RegistrationMembership.svg';
import { useFetchTopicQuery } from '../documents/topic.generated';
import { SeriesAccessType } from '../utils/formatSeriesAccessType';

interface RsvpProps {
  sessionId: string;
  topicSlug: string;
}

const PageWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: layout.containerWidth,
  margin: '0 auto',
});

const List = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const ActionItemWithoutMargin = styled(ActionItem)({
  marginTop: 0,
  '& + &': { marginTop: 0 },
  '&& + &&': { marginTop: 0 },
});

const RegisterMembershipCheck: React.FC<unknown> = () => {
  const history = useHistory();
  const analytics = useAnalytics();
  const { topicSlug, sessionId } = useParams<RsvpProps>();
  const { data, loading, error } = useFetchTopicQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      topicSlug: topicSlug,
    },
  });

  const [showError, setShowError] = useState(false);

  const isMembersOnly =
    data?.circleTopicBySlug?.series.accessType === SeriesAccessType.MODERN_HEALTH_MEMBERS_ONLY;

  useEffect(() => {
    if (error) {
      sentryCaptureException(error);
      captureException(error);
    }
  }, [error]);

  if (loading) return null;

  const numberOfCustomQuestions = data?.circleTopicBySlug?.rsvpQuestions.length;
  const memberRegistrationUrl =
    numberOfCustomQuestions && numberOfCustomQuestions > 0
      ? `${process.env.REACT_APP_MEMBER_WEB_URL}/circles/${topicSlug}/session/${sessionId}/register`
      : `${process.env.REACT_APP_MEMBER_WEB_URL}/circles/${topicSlug}`;

  return (
    <PageWrapper>
      <Image width={245} />
      <HeadingText50 css={{ marginBottom: 32 }}>Choose an option to continue</HeadingText50>
      <List>
        <ActionItemWithoutMargin
          title="I’m a Modern Health member"
          description="I have Modern Health as a benefit through my employer. Sign in."
          onClick={() => {
            analytics.trackClick('Circles: Membership Check', {
              isMembersOnly,
              membershipChoice: 'member',
              result: 'redirect',
              destination: memberRegistrationUrl,
            });

            window.location.href = memberRegistrationUrl;
          }}
          withBorder
        />
        <ActionItemWithoutMargin
          title="I’m a dependent of a Modern Health member"
          description="I'm an eligible dependent of a Modern Health member. Sign in."
          onClick={() => {
            analytics.trackClick('Circles: Membership Check', {
              isMembersOnly,
              membershipChoice: 'dependent',
              result: 'redirect',
              destination: memberRegistrationUrl,
            });

            window.location.href = memberRegistrationUrl;
          }}
          withBorder
        />
        <ActionItemWithoutMargin
          title="I’m a guest"
          description="You can join Circle sessions which are open to public. Continue as a guest."
          onClick={() => {
            if (isMembersOnly) {
              analytics.trackClick('Circles: Membership Check', {
                isMembersOnly,
                membershipChoice: 'guest',
                result: 'blocked',
                destination: null,
              });

              setShowError(true);
            } else {
              const internalPath = `/circle/${topicSlug}/rsvp/${sessionId}`;

              analytics.trackClick('Circles: Membership Check', {
                isMembersOnly,
                isMember: false,
                isDependent: false,
                result: 'form',
                destination: internalPath,
              });

              history.push(internalPath);
            }
          }}
          withBorder
        />
        {showError ? (
          <InlineMessage
            heading="Members Only"
            message="We're sorry, this Circle is reserved for members only."
          >
            <Link to="/upcoming" css={{ textDecoration: 'underline' }}>
              Explore upcoming circles
            </Link>
          </InlineMessage>
        ) : null}
        <Button
          title="Back to Circles"
          variant="secondary"
          size="large"
          fullWidth
          onClick={() => {
            history.goBack();
          }}
        />
      </List>
    </PageWrapper>
  );
};

export default RegisterMembershipCheck;
